<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { SIGNUP_URL } from "@/seo/config";

const { t } = useI18n();

const tierMeta = [
  {
    key: "solo" as const,
    monthly: "€5.99",
    annual: "€4.50",
    annualBilled: "€54/yr",
    recommended: false,
  },
  {
    key: "duo" as const,
    monthly: "€8.99",
    annual: "€6.75",
    annualBilled: "€81/yr",
    recommended: true,
  },
  {
    key: "family" as const,
    monthly: "€10.99",
    annual: "€8.25",
    annualBilled: "€99/yr",
    recommended: false,
  },
] as const;

const sharedFeatureKeys = [
  "routinePaths",
  "eventsChores",
  "reflections",
  "themes",
  "parentMode",
] as const;

const isYearly = ref(true);

function tierPrice(tier: (typeof tierMeta)[number]) {
  return isYearly.value
    ? { price: tier.annual, label: t("pricing.perMonth") }
    : { price: tier.monthly, label: t("pricing.perMonth") };
}
</script>

<template>
  <section id="pricing" class="bg-[var(--color-bg-secondary)] px-5 py-12 md:px-12 md:py-24">
    <div class="mx-auto max-w-[var(--max-w-page)] text-center">
      <h2
        data-reveal
        class="fade-up text-2xl font-semibold text-[var(--color-text-primary)] md:text-4xl"
        style="font-family: var(--font-display)"
      >
        {{ t("pricing.heading") }}
      </h2>
      <p
        data-reveal
        data-reveal-delay="1"
        class="fade-up mt-3 text-base text-[var(--color-text-secondary)] md:text-lg"
      >
        {{ t("pricing.subheading") }}
      </p>

      <!-- Monthly / Annual toggle -->
      <div
        data-reveal
        data-reveal-delay="2"
        class="fade-up mt-8 inline-flex items-center gap-3"
        role="radiogroup"
        :aria-label="t('pricing.billingLabel')"
      >
        <button
          role="radio"
          :aria-checked="!isYearly"
          :class="[
            'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
            !isYearly
              ? 'bg-[var(--color-primary)] text-white'
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]',
          ]"
          @click="isYearly = false"
        >
          {{ t("pricing.monthly") }}
        </button>
        <button
          role="radio"
          :aria-checked="isYearly"
          :class="[
            'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
            isYearly
              ? 'bg-[var(--color-primary)] text-white'
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]',
          ]"
          @click="isYearly = true"
        >
          {{ t("pricing.yearly") }}
          <span class="ml-1 text-xs opacity-80">{{ t("pricing.yearlyDiscount") }}</span>
        </button>
      </div>

      <!-- Tier cards -->
      <div class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div
          v-for="(tier, i) in tierMeta"
          :key="tier.key"
          data-reveal
          :data-reveal-delay="i + 2"
          :class="[
            'fade-up relative flex flex-col rounded-2xl bg-[var(--color-surface)] p-6 text-left md:p-8',
            tier.recommended
              ? 'border-2 border-[var(--color-primary)] shadow-md'
              : 'border border-[var(--color-border)] shadow-sm',
          ]"
        >
          <!-- Recommended badge -->
          <span
            v-if="tier.recommended"
            class="absolute -top-3.5 left-6 rounded-full bg-[var(--color-primary-light)] px-4 py-1 text-[13px] font-semibold text-[var(--color-primary)]"
          >
            {{ t("pricing.recommended") }}
          </span>

          <h3
            :class="[
              'text-[22px] font-semibold text-[var(--color-text-primary)]',
              tier.recommended ? 'mt-2' : '',
            ]"
          >
            {{ t(`pricing.${tier.key}.name`) }}
          </h3>
          <p class="mt-1 text-sm text-[var(--color-text-muted)]">
            {{ t(`pricing.${tier.key}.tagline`) }}
          </p>

          <!-- Price -->
          <p
            class="mt-4 text-4xl font-bold text-[var(--color-text-primary)]"
            style="font-family: var(--font-display)"
          >
            {{ tierPrice(tier).price
            }}<span class="text-lg font-normal text-[var(--color-text-muted)]">{{
              tierPrice(tier).label
            }}</span>
          </p>
          <p v-if="isYearly" class="text-sm text-[var(--color-text-muted)]">
            {{ t("pricing.billed", { amount: tier.annualBilled }) }}
          </p>

          <!-- Kids, history & routines -->
          <div class="mt-4 flex flex-col gap-2">
            <div class="flex items-center gap-2 text-[15px] text-[var(--color-text-secondary)]">
              <UIcon name="i-lucide-users" class="size-4 shrink-0 text-[var(--color-primary)]" />
              {{ t(`pricing.${tier.key}.kids`) }}
            </div>
            <div class="flex items-center gap-2 text-[15px] text-[var(--color-text-secondary)]">
              <UIcon name="i-lucide-clock" class="size-4 shrink-0 text-[var(--color-primary)]" />
              {{ t(`pricing.${tier.key}.history`) }}
            </div>
            <div class="flex items-center gap-2 text-[15px] text-[var(--color-text-secondary)]">
              <UIcon
                name="i-lucide-list-checks"
                class="size-4 shrink-0 text-[var(--color-primary)]"
              />
              {{ t(`pricing.${tier.key}.routines`) }}
            </div>
          </div>

          <hr class="my-6 border-[var(--color-border)]" />

          <!-- Shared features -->
          <ul class="flex-1 space-y-3">
            <li
              v-for="featureKey in sharedFeatureKeys"
              :key="featureKey"
              class="flex items-start gap-2.5 text-[15px] text-[var(--color-text-secondary)]"
            >
              <UIcon
                name="i-lucide-check"
                class="mt-0.5 size-4 shrink-0 text-[var(--color-success)]"
              />
              {{ t(`pricing.sharedFeatures.${featureKey}`) }}
            </li>
          </ul>

          <!-- CTA -->
          <a
            :href="SIGNUP_URL"
            :class="[
              'mt-8 block w-full rounded-full py-3.5 text-center text-base font-semibold transition-colors',
              tier.recommended
                ? 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]'
                : 'border-[1.5px] border-[var(--color-primary)] bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-primary-light)]',
            ]"
          >
            {{ t("pricing.startTrial") }}
          </a>
        </div>
      </div>

      <!-- Lifetime callout -->
      <div
        data-reveal
        data-reveal-delay="5"
        class="fade-up mx-auto mt-8 flex max-w-[920px] flex-col items-center gap-4 rounded-2xl bg-[var(--color-bg-dark)] px-8 py-8 text-center md:flex-row md:justify-between md:text-left"
      >
        <div>
          <div class="flex items-center justify-center gap-3 md:justify-start">
            <h3
              class="text-xl font-semibold text-[var(--color-sprout)]"
              style="font-family: var(--font-display)"
            >
              {{ t("pricing.lifetime.name") }}
            </h3>
            <span
              class="rounded-full border border-[rgba(240,180,41,0.3)] bg-[rgba(240,180,41,0.15)] px-3 py-0.5 text-xs font-semibold text-white"
            >
              {{ t("pricing.lifetime.badge") }}
            </span>
          </div>
          <p class="mt-2 text-[15px] text-[var(--color-mist)]">
            {{ t("pricing.lifetime.description") }}
          </p>
        </div>
        <a
          :href="SIGNUP_URL"
          class="shrink-0 rounded-full border-[1.5px] border-[var(--color-sprout)] px-8 py-3.5 text-base font-semibold text-[var(--color-sprout)] transition-colors hover:bg-[var(--color-sprout)] hover:text-[var(--color-bg-dark)]"
        >
          {{ t("pricing.lifetime.cta") }}
        </a>
      </div>

      <p
        data-reveal
        data-reveal-delay="5"
        class="fade-up mt-6 text-sm text-[var(--color-text-muted)]"
      >
        {{ t("pricing.footer") }}
      </p>
    </div>
  </section>
</template>
